import React, { createContext, useContext, useState, useCallback } from 'react';
import { AccountFormFields } from '../../../types';
import { UserProfileFormField } from 'src/utils/enum';

interface FormContextType {
    formData: Partial<AccountFormFields>;
    updateField: (field: keyof AccountFormFields, value: string) => void;
    updateFields: (fields: Partial<AccountFormFields>) => void;
    resetForm: () => void;
    isFormValid: boolean;
    fieldErrors: Record<string, string>;
    setFieldError: (field: keyof AccountFormFields, error: string) => void;
    clearFieldError: (field: keyof AccountFormFields) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

const initialFormData: Partial<AccountFormFields> = {
    [UserProfileFormField.Email]: '',
    [UserProfileFormField.ConfirmEmail]: '',
    [UserProfileFormField.FirstName]: '',
    [UserProfileFormField.LastName]: '',
    [UserProfileFormField.Password]: '',
    [UserProfileFormField.UserName]: '',
    [UserProfileFormField.ShirtSize]: '',
    [UserProfileFormField.BirthDate]: '',
    [UserProfileFormField.ProductID]: '',
};

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [formData, setFormData] = useState<Partial<AccountFormFields>>(initialFormData);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const updateField = useCallback((field: keyof AccountFormFields, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when field is updated
        if (fieldErrors[field]) {
            setFieldErrors(prev => {
                const { [field]: removed, ...rest } = prev;
                return rest;
            });
        }
    }, [fieldErrors]);

    const updateFields = useCallback((fields: Partial<AccountFormFields>) => {
        setFormData(prev => ({ ...prev, ...fields }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData(initialFormData);
        setFieldErrors({});
    }, []);

    const setFieldError = useCallback((field: keyof AccountFormFields, error: string) => {
        setFieldErrors(prev => ({ ...prev, [field]: error }));
    }, []);

    const clearFieldError = useCallback((field: keyof AccountFormFields) => {
        setFieldErrors(prev => {
            const { [field]: removed, ...rest } = prev;
            return rest;
        });
    }, []);

    // Basic form validation
    const isFormValid = Boolean(
        formData[UserProfileFormField.FirstName] &&
        formData[UserProfileFormField.LastName] &&
        formData[UserProfileFormField.Email] &&
        formData[UserProfileFormField.ConfirmEmail] &&
        formData[UserProfileFormField.Password] &&
        formData[UserProfileFormField.UserName] &&
        Object.keys(fieldErrors).length === 0
    );

    const value: FormContextType = {
        formData,
        updateField,
        updateFields,
        resetForm,
        isFormValid,
        fieldErrors,
        setFieldError,
        clearFieldError,
    };

    return (
        <FormContext.Provider value={value}>
            {children}
        </FormContext.Provider>
    );
};

export const useFormContext = (): FormContextType => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error('useFormContext must be used within a FormProvider');
    }
    return context;
};
